import React, { useState, useEffect } from 'react'
import {
  Card, Row, Col, DatePicker, Button, Space, Table, Statistic, message, Tabs, Progress
} from 'antd'
import {
  DownloadOutlined, ReloadOutlined, UserOutlined, MessageOutlined,
  NodeIndexOutlined, PictureOutlined
} from '@ant-design/icons'
import { conversationApi, analyticsApi } from '@/services/api'
import ReactECharts from 'echarts-for-react'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

interface UserBehaviorData {
  user_id: string
  total_conversations: number
  total_messages: number
  total_tokens: number
  total_cost: number
  avg_session_length: number
  canvas_adoption_rate: string | number
  images_usage_rate: string | number
  client_types: string[]
  client_types_list?: string[]
  models_used: string[]
  first_activity: number
  last_activity: number
  activity_days: number
  peak_hour: number
}

interface ActivityPattern {
  hour: number
  activity_count: number
  user_count: number
}

interface ClientDistribution {
  client_type: string
  user_count: number
  session_count: number
  percentage: number
}

const UserBehaviorPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [userBehaviorData, setUserBehaviorData] = useState<UserBehaviorData[]>([])
  const [activityPatterns, setActivityPatterns] = useState<ActivityPattern[]>([])
  const [clientDistribution, setClientDistribution] = useState<ClientDistribution[]>([])
  const [activeTab, setActiveTab] = useState('overview')

  // 统计数据
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    avgSessionsPerUser: 0,
    avgTokensPerUser: 0,
    canvasAdoptionRate: 0,
    imagesUsageRate: 0
  })

  useEffect(() => {
    fetchData()
  }, [dateRange])

  const fetchData = async () => {
    try {
      setLoading(true)

      const params = {
        dateRange: dateRange ? [
          dateRange[0].valueOf(),
          dateRange[1].valueOf()
        ] as [number, number] : undefined
      }

      // 🔥 修复：获取用户行为数据 - 使用正确的API
      const [behaviorResponse, clientStatsResponse] = await Promise.all([
        analyticsApi.getUserBehaviorAnalysis(params),
        conversationApi.getClientUsageStats(params)
      ])

      console.log('🔍 用户行为数据响应:', behaviorResponse)
      console.log('🔍 客户端统计响应:', clientStatsResponse)

      if (behaviorResponse.success) {
        // 🔥 修复：处理不同的数据结构
        const responseData = behaviorResponse.data || {}
        let userData = []

        // 检查数据结构并适配
        if (Array.isArray(responseData.users)) {
          userData = responseData.users
        } else if (Array.isArray(responseData)) {
          userData = responseData
        } else if (responseData.data && Array.isArray(responseData.data)) {
          userData = responseData.data
        } else {
          console.warn('⚠️ 用户数据结构异常:', responseData)
          userData = []
        }

        console.log('📊 处理后的用户数据:', userData)
        setUserBehaviorData(userData)

        // 计算统计数据
        const totalUsers = userData.length
        const activeUsers = userData.filter((u: any) => (u.conversation_count || 0) > 0).length
        const totalSessions = userData.reduce((sum: number, u: any) => sum + (u.conversation_count || 0), 0)
        const totalTokens = userData.reduce((sum: number, u: any) => sum + (u.total_tokens || 0), 0)

        // 🔥 修复：基于实际数据计算Canvas和图片使用情况
        const canvasUsers = userData.filter((u: any) => (u.canvas_sessions || 0) > 0).length
        const imageUsers = userData.filter((u: any) => (u.image_messages || 0) > 0).length

        setStats({
          totalUsers,
          activeUsers,
          avgSessionsPerUser: totalUsers > 0 ? Math.round(totalSessions / totalUsers) : 0,
          avgTokensPerUser: totalUsers > 0 ? Math.round(totalTokens / totalUsers) : 0,
          canvasAdoptionRate: totalUsers > 0 ? Math.round((canvasUsers / totalUsers) * 100) : 0,
          imagesUsageRate: totalUsers > 0 ? Math.round((imageUsers / totalUsers) * 100) : 0
        })

        console.log('📈 计算的统计数据:', {
          totalUsers,
          activeUsers,
          canvasUsers,
          imageUsers,
          totalSessions,
          totalTokens
        })

        // 生成活动模式数据（模拟）
        generateActivityPatterns(userData)
      }

      if (clientStatsResponse.success) {
        // 🔥 修复：确保clientData是数组类型
        const clientData = Array.isArray(clientStatsResponse.data) ? clientStatsResponse.data : []
        setClientDistribution(clientData.map(item => ({
          client_type: item.client_type || 'unknown',
          user_count: item.unique_users || 0,
          session_count: item.total_conversations || 0,
          percentage: parseFloat(item.usage_percentage || '0')
        })))
      }

    } catch (error) {
      console.error('获取用户行为数据失败:', error)
      message.error('获取数据失败')
    } finally {
      setLoading(false)
    }
  }

  const generateActivityPatterns = (userData: UserBehaviorData[]) => {
    // 模拟24小时活动模式
    const patterns: ActivityPattern[] = []
    for (let hour = 0; hour < 24; hour++) {
      // 基于用户数据生成模拟的活动模式
      const baseActivity = Math.sin((hour - 6) * Math.PI / 12) * 0.5 + 0.5
      const activityCount = Math.round(baseActivity * userData.length * 0.3)
      const userCount = Math.round(activityCount * 0.7)
      
      patterns.push({
        hour,
        activity_count: Math.max(0, activityCount),
        user_count: Math.max(0, userCount)
      })
    }
    setActivityPatterns(patterns)
  }

  // 活动模式图表配置
  const getActivityPatternChart = () => ({
    title: {
      text: '24小时活动模式',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['活动次数', '活跃用户'],
      top: 30
    },
    xAxis: {
      type: 'category',
      data: activityPatterns.map(p => `${p.hour}:00`),
      name: '时间'
    },
    yAxis: [
      {
        type: 'value',
        name: '活动次数',
        position: 'left'
      },
      {
        type: 'value',
        name: '用户数',
        position: 'right'
      }
    ],
    series: [
      {
        name: '活动次数',
        type: 'line',
        data: activityPatterns.map(p => p.activity_count),
        smooth: true,
        itemStyle: { color: '#1890ff' }
      },
      {
        name: '活跃用户',
        type: 'bar',
        yAxisIndex: 1,
        data: activityPatterns.map(p => p.user_count),
        itemStyle: { color: '#52c41a' }
      }
    ]
  })

  // 客户端分布图表配置
  const getClientDistributionChart = () => ({
    title: {
      text: '客户端使用分布',
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      top: 50
    },
    series: [
      {
        name: '用户数',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['60%', '60%'],
        data: clientDistribution.map(item => ({
          value: item.user_count,
          name: item.client_type
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  })

  // 用户行为表格列配置
  const columns = [
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 200,
      render: (text: string) => (
        <span title={text}>
          {text.length > 20 ? `${text.substring(0, 20)}...` : text}
        </span>
      )
    },
    {
      title: '会话数',
      dataIndex: 'conversation_count',
      key: 'conversation_count',
      sorter: (a: any, b: any) => (a.conversation_count || 0) - (b.conversation_count || 0)
    },
    {
      title: '消息数',
      dataIndex: 'message_count',
      key: 'message_count',
      sorter: (a: any, b: any) => (a.message_count || 0) - (b.message_count || 0)
    },
    {
      title: 'Token数',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      render: (value: number) => value?.toLocaleString() || 0,
      sorter: (a: any, b: any) => (a.total_tokens || 0) - (b.total_tokens || 0)
    },
    {
      title: 'Canvas采用率',
      dataIndex: 'canvas_adoption_rate',
      key: 'canvas_adoption_rate',
      render: (value: string) => (
        <Progress
          percent={parseFloat(value) || 0}
          size="small"
          format={percent => `${percent}%`}
        />
      ),
      sorter: (a: UserBehaviorData, b: UserBehaviorData) =>
        parseFloat(String(a.canvas_adoption_rate)) - parseFloat(String(b.canvas_adoption_rate))
    },
    {
      title: '图片使用率',
      dataIndex: 'images_usage_rate',
      key: 'images_usage_rate',
      render: (value: string) => (
        <Progress
          percent={parseFloat(value) || 0}
          size="small"
          format={percent => `${percent}%`}
          strokeColor="#fa8c16"
        />
      ),
      sorter: (a: UserBehaviorData, b: UserBehaviorData) =>
        parseFloat(String(a.images_usage_rate)) - parseFloat(String(b.images_usage_rate))
    },
    {
      title: '客户端类型',
      dataIndex: 'client_types_list',
      key: 'client_types_list',
      render: (types: string[]) => (
        <Space size={4} wrap>
          {(types || []).slice(0, 2).map(type => (
            <span key={type} style={{ 
              fontSize: '12px', 
              padding: '2px 6px', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '4px' 
            }}>
              {type}
            </span>
          ))}
          {(types || []).length > 2 && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              +{types.length - 2}
            </span>
          )}
        </Space>
      )
    },
    {
      title: '总费用',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (value: any) => {
        const numValue = Number(value) || 0;
        return `$${numValue.toFixed(4)}`;
      },
      sorter: (a: any, b: any) => (Number(a.total_cost) || 0) - (Number(b.total_cost) || 0)
    }
  ]

  const handleExport = () => {
    // 导出功能实现
    const csvContent = [
      ['用户ID', '会话数', '消息数', 'Token数', 'Canvas采用率', '图片使用率', '总费用'].join(','),
      ...userBehaviorData.map(item => [
        item.user_id,
        item.total_conversations,
        item.total_messages,
        item.total_tokens,
        item.canvas_adoption_rate,
        item.images_usage_rate,
        item.total_cost
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `user-behavior-${dayjs().format('YYYY-MM-DD')}.csv`
    link.click()
    
    message.success('数据导出成功')
  }

  return (
    <div style={{ padding: '0' }}>
      <Card title="用户行为分析" extra={
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            刷新
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            导出
          </Button>
        </Space>
      }>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: '概览统计',
              children: (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={4}>
                    <Statistic
                      title="总用户数"
                      value={stats.totalUsers}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="活跃用户"
                      value={stats.activeUsers}
                      prefix={<MessageOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="平均会话数"
                      value={stats.avgSessionsPerUser}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="平均Token数"
                      value={stats.avgTokensPerUser}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="Canvas采用率"
                      value={stats.canvasAdoptionRate}
                      suffix="%"
                      prefix={<NodeIndexOutlined />}
                      valueStyle={{ color: '#13c2c2' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Statistic
                      title="图片使用率"
                      value={stats.imagesUsageRate}
                      suffix="%"
                      prefix={<PictureOutlined />}
                      valueStyle={{ color: '#eb2f96' }}
                    />
                  </Col>
                </Row>
              )
            },
            {
              key: 'activity',
              label: '活动模式',
              children: (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={24}>
                    <Card title="24小时活动模式分析" size="small">
                      <ReactECharts
                        option={getActivityPatternChart()}
                        style={{ width: '100%', height: '400px' }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    </Card>
                  </Col>
                </Row>
              )
            },
            {
              key: 'clients',
              label: '客户端分布',
              children: (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card title="客户端使用分布" size="small">
                      <ReactECharts
                        option={getClientDistributionChart()}
                        style={{ width: '100%', height: '400px' }}
                        notMerge={true}
                        lazyUpdate={true}
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="客户端统计详情" size="small">
                      <Table
                        columns={[
                          { title: '客户端', dataIndex: 'client_type', key: 'client_type' },
                          { title: '用户数', dataIndex: 'user_count', key: 'user_count' },
                          { title: '会话数', dataIndex: 'session_count', key: 'session_count' },
                          {
                            title: '占比',
                            dataIndex: 'percentage',
                            key: 'percentage',
                            render: (value: any) => {
                              const numValue = Number(value) || 0;
                              return `${numValue.toFixed(1)}%`;
                            }
                          }
                        ]}
                        dataSource={clientDistribution}
                        rowKey="client_type"
                        pagination={false}
                        size="small"
                      />
                    </Card>
                  </Col>
                </Row>
              )
            },
            {
              key: 'details',
              label: '用户详情',
              children: (
                <Table
                  columns={columns}
                  dataSource={userBehaviorData}
                  loading={loading}
                  rowKey="user_id"
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                  }}
                  scroll={{ x: 1200 }}
                />
              )
            }
          ]}
        />
      </Card>
    </div>
  )
}

export default UserBehaviorPage
